import useUserUiPreferenceStorageValue from "./useUserUiPreferenceStorageValue.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { MIN_DAY_RAIL_WIDTH, MIN_EVENING_RAIL_WIDTH, persistEveningRailWidth, readEveningRailWidth, resolveEveningRailLayout } from "../utils/eveningRailWidth.js";

export default function useEveningRailWidth({ enabled, userId }) {
  const [element, setElement] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [preference, setPreference] = useState(() => ({ userId, ratio: readEveningRailWidth(userId) }));
  const cleanupRef = useRef(null);
  const storedWidth = useUserUiPreferenceStorageValue("local", `grain-saas:results:evening-rail-width:v1:${encodeURIComponent(userId || "")}`);
  const ratio = preference.userId === userId ? preference.ratio : readEveningRailWidth(userId);
  const layout = resolveEveningRailLayout(ratio, containerWidth);

  useEffect(() => {
    if (!cleanupRef.current) setPreference({ userId, ratio: readEveningRailWidth(userId) });
  }, [userId, storedWidth]);
  useEffect(() => {
    if (!enabled || !element) return undefined;
    const measure = () => setContainerWidth(element.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, element]);
  useEffect(() => () => cleanupRef.current?.(false), [enabled, element, userId]);

  const persistWidth = useCallback((width) => {
    if (!layout.resizable) return;
    const nextRatio = resolveEveningRailLayout(width / layout.width, containerWidth).ratio;
    setPreference({ userId, ratio: nextRatio });
    persistEveningRailWidth(userId, nextRatio);
  }, [containerWidth, layout.resizable, layout.width, userId]);

  const onPointerDown = (event) => {
    if (!enabled || !layout.resizable || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current?.(false);
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startWidth = layout.eveningWidth;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    let pendingRatio = layout.ratio;
    let frame = null;
    let moved = false;
    const update = () => {
      frame = null;
      setPreference({ userId, ratio: pendingRatio });
    };
    const move = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const nextWidth = Math.min(layout.width - MIN_DAY_RAIL_WIDTH, Math.max(MIN_EVENING_RAIL_WIDTH, startWidth + startX - moveEvent.clientX));
      pendingRatio = nextWidth / layout.width;
      moved ||= Math.abs(moveEvent.clientX - startX) > 2;
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };
    const cleanup = (commit) => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", cancel, true);
      window.removeEventListener("blur", cancel);
      cleanupRef.current = null;
      if (commit) {
        update();
        persistEveningRailWidth(userId, pendingRatio);
        if (moved) {
          document.addEventListener("click", suppressClick, { capture: true, once: true });
          window.setTimeout(() => document.removeEventListener("click", suppressClick, true), 0);
        }
      } else {
        setPreference({ userId, ratio });
      }
    };
    const up = (upEvent) => { if (upEvent.pointerId === pointerId) cleanup(true); };
    const cancel = () => cleanup(false);
    cleanupRef.current = cleanup;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", cancel, true);
    window.addEventListener("blur", cancel);
  };

  const onKeyDown = (event) => {
    const delta = event.key === "ArrowLeft" ? 10 : event.key === "ArrowRight" ? -10 : 0;
    if (!delta && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    event.stopPropagation();
    persistWidth(event.key === "Home" ? MIN_EVENING_RAIL_WIDTH
      : event.key === "End" ? layout.width - MIN_DAY_RAIL_WIDTH
        : layout.eveningWidth + delta);
  };

  return { ...layout, scaleWidth: containerWidth || undefined, containerRef: setElement, onPointerDown, onKeyDown };
}
